import React, { useState } from "react";
import { useForm, Controller, type SubmitHandler } from "react-hook-form";

// Components //
import TitleSection from "../../molecules/title-section";
import FormInputAceleradora from "../../atoms/form-input-aceleradora/FormInputAceleradora";
import Button from "../../atoms/button/button";
import ArrowRigth from "../../atoms/icons/ArrowRigth";
import Loading from "../../atoms/loading/loading";

// Utils //
import rawToReact from "../../../utils/rawToReact";

// Interfaces //
import { ReferenciaFormulario } from "../../../interfaces/ReferenciaContenido";

// Styles //
import "./EnterpriseMentorshipValidatorForm.scss";

//services //
import { getEnterpriseMentorshipValidator } from "../../../services/enterpriseServices";

interface EnterpriseMentorshipValidatorFormProps {
    referenciaFormulario: ReferenciaFormulario;
    onMentoringValidator: (data: any) => void;
}

type FormValues = {
    documentNumber: string;
};

const MAIN_CLASS = "o-enterprise-mentorship-validator-form";

export function getValidationRules(fieldName: string) {
    if (fieldName === "documentNumber") {
        return {
            required: "El NIT es obligatorio",
            pattern: {
                value: /^[0-9]+$/,
                message: "El NIT solo debe contener números",
            },
            minLength: {
                value: 5,
                message: "El NIT debe tener al menos 5 dígitos",
            },
            maxLength: {
                value: 15,
                message: "El NIT no debe superar los 15 dígitos",
            },
        };
    }

    return {};
}

const EnterpriseMentorshipValidatorForm = ({
        referenciaFormulario,
        onMentoringValidator,
    }: EnterpriseMentorshipValidatorFormProps) => {
    const form = useForm<FormValues>({
        defaultValues: {
            documentNumber: "",
        },
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const values = form.watch();

    const formContent = referenciaFormulario?.titulosFormulario?.[0] || {};

    const formIsValid = values?.documentNumber?.length > 3;

    const formHasErrors = form.formState.errors?.documentNumber !== undefined;

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        setIsLoading(true);
        try{
            const response = await getEnterpriseMentorshipValidator(data.documentNumber)
            // Siempre pasa la respuesta completa al padre
            onMentoringValidator(response.data);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            onMentoringValidator({
                status: 500,
                userMessage: "Error de red o servidor",
                data: null,
            });
        }
    };

    const handleValueChange = (nombreId: string, value: string) => {
        form.setValue(nombreId as any, value);
    };

    return (
        <section className={MAIN_CLASS}>
            <TitleSection
                title={referenciaFormulario?.tituloFormulario}
                type="h1"
            />

            <div className={`${MAIN_CLASS}__description`}>
                {rawToReact(referenciaFormulario?.descripcionFormulario)}
            </div>

            <form
                className={`${MAIN_CLASS}__form`}
                onSubmit={form.handleSubmit(onSubmit)}
            >
                {formContent?.referenciaFormulario?.map((input) => {
                    return (
                        <div
                            className={`${MAIN_CLASS}__form__field`}
                            key={input.contentful_id}
                        >
                            <Controller
                                name={input.nombreId as any}
                                control={form.control}
                                rules={{
                                    required: input.mensajeError,
                                    ...getValidationRules(input.nombreId),
                                }}
                                render={({ fieldState, field }) => (
                                    <FormInputAceleradora
                                        title={input.inputPrincipal}
                                        placeholder={input.placeholder}
                                        isRequired={input.obligatorio}
                                        value={values[input.nombreId]}
                                        nombreId={input.nombreId}
                                        textoTerminos={undefined}
                                        type={"number"}
                                        setInputValue={(name, value) => {
                                            handleValueChange(
                                                input.nombreId,
                                                value
                                            );
                                            field.onChange(value);
                                        }}
                                        isError={
                                            fieldState?.error !== undefined
                                        }
                                        errorMessage={
                                            fieldState?.error?.message
                                        }
                                    />
                                )}
                            />
                            {isLoading && (
                                <div className="loading">
                                    <Loading />
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className={`${MAIN_CLASS}__form__button`}>
                    <Button
                        type="button"
                        classname="a-button__magenta big center"
                        text={formContent.tituloBoton}
                        isDisabled={!formIsValid || formHasErrors}
                    />
                </div>

                <div className={`${MAIN_CLASS}__form__back-button`}>
                    <Button
                        classname="a-button__magenta back-button"
                        type={"link"}
                        text={"Volver"}
                        href="/servicio-de-empleo/empresas/mentoria-empresarial"
                        isDisabled={undefined}
                    >
                        <ArrowRigth />
                    </Button>
                </div>
            </form>
        </section>
    );
};

export default EnterpriseMentorshipValidatorForm;
